import { makeAutoObservable } from 'mobx';
import { getStudioConfigs, StudioConfigsResponse } from '../api/API';
import { toZonedTime } from 'date-fns-tz';

export default class StudioConfigStore {
  public studioConfigs: StudioConfigsResponse | null = null;
  public selectedDate: Date = toZonedTime(new Date(), 'Europe/Tallinn');
  public isLoading: boolean = false;
  public error: string | null = null;
  public lastFetchTime: number = 0;
  private readonly STALE_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes
  private isTabActive: boolean = !document.hidden;

  constructor() {
    makeAutoObservable(this);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private handleVisibilityChange = () => {
    this.isTabActive = !document.hidden;
  }

  get isDataStale(): boolean {
    if (!this.isTabActive) {
      return false;
    }
    const now = Date.now();
    const timeSinceLastFetch = now - this.lastFetchTime;
    const isStale = timeSinceLastFetch > this.STALE_THRESHOLD_MS;
    
    return isStale;
  }

  async fetchStudioConfigs(forceRefresh = false) {
    if (this.studioConfigs && !forceRefresh && !this.isDataStale) {
      return this.studioConfigs;
    }

    this.isLoading = true;
    this.error = null;

    try {
      const configs = await getStudioConfigs();
      this.studioConfigs = configs;
      this.selectedDate = toZonedTime(new Date(configs.serverDateTime), 'Europe/Tallinn');
      this.lastFetchTime = Date.now();
      return configs;
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Failed to fetch studio configs';
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  setSelectedDate(date: Date) {
    this.selectedDate = date;
  }

  resetSelectedDate() {
    const date = this.studioConfigs?.serverDateTime
      ? new Date(this.studioConfigs.serverDateTime)
      : new Date();
    this.selectedDate = toZonedTime(date, 'Europe/Tallinn');
  }

  get checkInFutureDays(): number {
    return this.studioConfigs?.checkInFutureDays || 7;
  }

  get shouldShowDateSelector(): boolean {
    return Number.isInteger(this.checkInFutureDays) && this.checkInFutureDays >= 2;
  }

  get appBaseUrl(): string {
    return this.studioConfigs?.appBaseUrl || '';
  }

  get studioName(): string {
    return this.studioConfigs?.studioName || '';
  }

  get currencySymbol(): string {
    return this.studioConfigs?.currencySymbol || '€';
  }

  get studioHomepageStore(): string {
    return this.studioConfigs?.studioHomepageStore || '';
  }

  get studioHomepage(): string {
    return this.studioConfigs?.studioHomepage || this.studioHomepageStore || '';
  }

  // Room rental configs
  get rentFutureDays(): number {
    return this.studioConfigs?.rentFutureDays || 14;
  }

  get rent1hPrice(): number {
    return this.studioConfigs?.rent1hPrice || 125;
  }

  get rent1h30mPrice(): number {
    return this.studioConfigs?.rent1h30mPrice || 165;
  }

  get rent2hPrice(): number {
    return this.studioConfigs?.rent2hPrice || 200;
  }

  get rent2h30mPrice(): number {
    return this.studioConfigs?.rent2h30mPrice || 225;
  }

  get rent3hPrice(): number {
    return this.studioConfigs?.rent3hPrice || 250;
  }

  get rentEnabled(): boolean {
    return this.studioConfigs?.rentEnabled || false;
  }

  get roomRentalDescription(): string {
    return this.studioConfigs?.roomRentalDescription || '';
  }

  get classCancelCoins(): number {
    return this.studioConfigs?.classCancelCoins || 30;
  }

  get classCancelBeforeHours(): number {
    return this.studioConfigs?.classCancelBeforeHours || 4;
  }

  get defaultTrainingPackageRecommendation(): string {
    return this.studioConfigs?.defaultTrainingPackageRecommendation?.trim() || 'S';
  }

  get packagesLowThresholdCoins(): number {
    const threshold = this.studioConfigs?.packagesLowThresholdCoins;
    return Number.isInteger(threshold) && threshold >= 0 ? threshold : 250;
  }

  get packagesExpiringThresholdDays(): number {
    const threshold = this.studioConfigs?.packagesExpiringThresholdDays;
    return Number.isInteger(threshold) && threshold >= 0 ? threshold : 7;
  }

  isPrivateClassesAllowed(userId?: number | null): boolean {
    if (userId == null) {
      return false;
    }
    return this.studioConfigs?.privateClassesEnabled === true;
  }

  getRoomRentServices() {
    return [
      { type: '1h', name: 'Rent 1h', duration: 60, price: this.rent1hPrice },
      { type: '1h30m', name: 'Rent 1h30m', duration: 90, price: this.rent1h30mPrice },
      { type: '2h', name: 'Rent 2h', duration: 120, price: this.rent2hPrice },
      { type: '2h30m', name: 'Rent 2h30m', duration: 150, price: this.rent2h30mPrice },
      { type: '3h', name: 'Rent 3h', duration: 180, price: this.rent3hPrice }
    ];
  }

  cleanup() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }
}
